import { useEffect, useRef, useState } from 'react';
import { LiveKitRoom, RoomAudioRenderer } from '@livekit/components-react';
import { Paperclip, Smile } from 'lucide-react';
import type { ConnectionDetails } from './livekit.api';
import TopBar from './TopBar';
import VideoStage from './VideoStage';
import Toolbar from './Toolbar';
import ParticipantsSidebar from './ParticipantsSidebar';
import AnnotationCanvas from './AnnotationCanvas';
import RecordControl from './RecordControl';
import Whiteboard from '@/features/whiteboard/Whiteboard';
import { useMeetingChat } from './useMeetingChat';
import { connectSocket } from '@/lib/socket';

const QUICK_EMOJIS = ['👍', '❤️', '😂', '🎉', '👏', '🙌'];

type Panel = 'none' | 'people' | 'chat';

function RoomBody({
  meetingId,
  title,
  details,
}: {
  meetingId: string;
  title: string;
  details: ConnectionDetails;
}): JSX.Element {
  const [panel, setPanel] = useState<Panel>('none');
  const [annotating, setAnnotating] = useState(false);
  const [whiteboard, setWhiteboard] = useState(false);
  const [recording, setRecording] = useState(false);
  const toggle = (p: Panel) => setPanel((cur) => (cur === p ? 'none' : p));

  useEffect(() => {
    const socket = connectSocket();
    const onStatus = ({ recording: r }: { recording: boolean }) => setRecording(r);
    socket.on('recording:status', onStatus);
    return () => {
      socket.off('recording:status', onStatus);
    };
  }, []);

  return (
    <div className="flex h-screen flex-col bg-slate-950">
      <TopBar
        title={title}
        recording={recording}
        extra={details.isModerator ? <RecordControl meetingId={meetingId} /> : undefined}
      />
      <div className="flex min-h-0 flex-1">
        <div className="relative min-w-0 flex-1">
          <VideoStage />
          {annotating && <AnnotationCanvas meetingId={meetingId} onClose={() => setAnnotating(false)} />}
          {whiteboard && <Whiteboard meetingId={meetingId} onClose={() => setWhiteboard(false)} />}
        </div>
        {panel !== 'none' && (
          <aside className="flex w-80 flex-col border-l border-slate-800 bg-slate-900 text-slate-200">
            <div className="border-b border-slate-800 px-4 py-2 text-sm font-semibold">
              {panel === 'people' ? 'Participants' : 'Chat'}
            </div>
            {panel === 'people' ? (
              <ParticipantsSidebar meetingId={meetingId} isModerator={details.isModerator} />
            ) : (
              <ChatPanel meetingId={meetingId} participantId={details.identity} />
            )}
          </aside>
        )}
      </div>
      <Toolbar
        onToggleParticipants={() => toggle('people')}
        onToggleChat={() => toggle('chat')}
        onToggleAnnotation={() => setAnnotating((a) => !a)}
        annotating={annotating}
        onToggleWhiteboard={() => setWhiteboard((w) => !w)}
      />
      <RoomAudioRenderer />
    </div>
  );
}

/** Socket-backed meeting chat: history, live messages, typing, reactions, files. */
function ChatPanel({ meetingId, participantId }: { meetingId: string; participantId: string }): JSX.Element {
  const { messages, typingPeers, send, react, setTyping, uploadFile } = useMeetingChat(meetingId, participantId);
  const [text, setText] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);
  const typingTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const onChange = (v: string) => {
    setText(v);
    setTyping(true);
    if (typingTimeout.current) clearTimeout(typingTimeout.current);
    typingTimeout.current = setTimeout(() => setTyping(false), 1500);
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex-1 space-y-2 overflow-y-auto p-3 text-sm">
        {messages.map((m) => (
          <div key={m.id} className="group rounded-lg bg-slate-800 px-3 py-2">
            <span className="text-xs text-slate-400">{m.senderId ?? 'Guest'}</span>
            {m.type === 'IMAGE' && m.fileUrl ? (
              <a href={m.fileUrl} target="_blank" rel="noreferrer">
                <img src={m.fileUrl} alt={m.fileName ?? ''} className="mt-1 max-h-40 rounded" />
              </a>
            ) : m.fileUrl ? (
              <a href={m.fileUrl} target="_blank" rel="noreferrer" className="mt-1 block text-brand-400 underline">
                📎 {m.fileName}
              </a>
            ) : (
              <p className="whitespace-pre-wrap break-words">{m.content}</p>
            )}
            <div className="mt-1 flex items-center gap-1">
              {Object.entries(m.reactions ?? {}).map(([emoji, users]) => (
                <span key={emoji} className="rounded-full bg-slate-700 px-1.5 text-xs">
                  {emoji} {users.length}
                </span>
              ))}
              <div className="hidden gap-0.5 group-hover:flex">
                {QUICK_EMOJIS.map((e) => (
                  <button key={e} className="text-xs hover:scale-125" onClick={() => react(m.id, e)}>
                    {e}
                  </button>
                ))}
              </div>
            </div>
          </div>
        ))}
        {typingPeers.length > 0 && <p className="text-xs italic text-slate-500">Someone is typing…</p>}
      </div>

      <form
        className="flex items-center gap-2 border-t border-slate-800 p-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (text.trim()) send(text.trim());
          setText('');
          setTyping(false);
        }}
      >
        <input
          ref={fileRef}
          type="file"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void uploadFile(f);
            e.target.value = '';
          }}
        />
        <button type="button" className="text-slate-400 hover:text-slate-200" onClick={() => fileRef.current?.click()}>
          <Paperclip size={18} />
        </button>
        <input
          className="flex-1 rounded-lg bg-slate-800 px-3 py-2 text-sm outline-none"
          value={text}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Message…"
        />
        <button type="submit" className="rounded-lg bg-brand-600 px-3 py-2 text-sm text-white">
          <Smile size={16} />
        </button>
      </form>
    </div>
  );
}

export default function MeetingRoom({
  meetingId,
  title,
  details,
  onLeave,
}: {
  meetingId: string;
  title: string;
  details: ConnectionDetails;
  onLeave: () => void;
}): JSX.Element {
  return (
    <LiveKitRoom
      serverUrl={details.serverUrl}
      token={details.token}
      connect
      video
      audio
      onDisconnected={onLeave}
      data-lk-theme="default"
      style={{ height: '100vh' }}
    >
      <RoomBody meetingId={meetingId} title={title} details={details} />
    </LiveKitRoom>
  );
}
